import { useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import type { TeamDetailResponse } from "@aura/shared";
import { Columns, Figure, Ledger, RankedBars, ReferenceDots } from "~/components/charts";
import { Banner, ed, tbl } from "~/components/editorial";
import { useIsAdmin } from "~/lib/auth";
import { usePeriod } from "~/lib/period";
import {
  changeText,
  fmtDay,
  fmtHours,
  fmtNum,
  fmtPct,
  periodName,
  plural,
  priorName,
  capitalize,
} from "~/lib/format";
import { MemberActions, TeamAdminBar } from "./components/admin";
import { TeamReadSection, TeamsMasthead, Was } from "./components/parts";
import { usePinMember } from "./mutations";
import {
  displayKey,
  paceIndex,
  perPerson,
  pointChange,
  rosterCounts,
  rosterOrder,
  shareText,
  weeklySeries,
} from "./model";
import { useTeamDetail, useTeamInsights } from "./queries";
import { buildTeamDek, buildTeamHeadline, sourceSentence } from "./story";

const SIGNAL_LABEL: Record<TeamDetailResponse["team"]["signal"], string> = {
  jira_project: "From a JIRA project",
  repo_cluster: "From shared repos",
  review_cluster: "From review patterns",
  manual: "Set by an admin",
};

export function TeamDetailPage() {
  const { teamId } = useParams<{ teamId: string }>();
  const [searchParams] = useSearchParams();
  const suffix = searchParams.toString() ? `?${searchParams.toString()}` : "";
  const { range, window } = usePeriod();
  const { data, isLoading, error } = useTeamDetail(teamId, range);
  const isAdmin = useIsAdmin();
  // The team list feeds the "Move to team" picker and the inference window.
  const { data: list } = useTeamInsights(range);
  const otherTeams = (list?.teams ?? [])
    .filter((t) => t.id !== teamId)
    .map((t) => ({ id: t.id, name: t.name }));

  const period = data?.period ?? { label: window.label, prevLabel: window.prevLabel };
  const now = periodName(period.label);
  const prior = priorName(period.prevLabel);

  const model = useMemo(() => {
    if (!data) return null;
    const { team, orgMedian, members } = data;
    const { current: orgNow, previous: orgBefore } = data.norm.org;
    const counts = rosterCounts(members);
    const pp = team.current.prsMergedPerPerson ?? perPerson(team.current.prsMerged, team.current.activeCount);
    return {
      counts,
      story: {
        name: team.name,
        periodLabel: period.label,
        prevLabel: period.prevLabel,
        prsMerged: team.current.prsMerged,
        prevPrsMerged: team.previous.prsMerged,
        headcount: team.current.headcount,
        sharedCount: counts.shared,
        perPerson: pp,
        orgPerPerson: orgMedian.prsMergedPerPerson,
        medianHoursToFirstReview: team.current.medianHoursToFirstReview,
        orgMedianHoursToFirstReview: orgMedian.medianHoursToFirstReview,
        leads: team.leads.map((l) => l.displayName),
        signal: team.signal,
        jiraProjectKey: team.jiraProjectKey,
        repoCount: team.repos.length,
        paceNow: paceIndex(team.current, orgNow),
        paceBefore: paceIndex(team.previous, orgBefore),
        score: team.current.score,
        orgScore: orgNow.score,
      },
    };
  }, [data, period.label, period.prevLabel]);

  return (
    <div className={ed.page}>
      <div className={ed.inner}>
        <TeamsMasthead periodLabel={period.label} prevLabel={period.prevLabel} />
        <Link to={`/teams${suffix}`} className={ed.back}>
          ← All teams
        </Link>

        {isLoading ? (
          <div className={ed.loading}>Loading…</div>
        ) : error || !data || !model ? (
          <div className={ed.loading}>Team not found.</div>
        ) : (
          <>
            <div className={ed.kicker}>
              {displayKey(data.team.key) ?? data.team.name} · {SIGNAL_LABEL[data.team.signal]} · {now}
              {data.team.hidden && " · Hidden from comparisons"}
            </div>
            <h1 className={ed.headline}>{buildTeamHeadline(model.story)}</h1>
            <p className={ed.dek}>{buildTeamDek(model.story)}</p>
            <div className={ed.byline}>
              <b>{data.team.name}</b> · <span className="num">{fmtNum(data.team.current.headcount)}</span>{" "}
              people, <span className="num">{fmtNum(data.team.current.activeCount)}</span> with work{" "}
              in {now} · compared with {prior}
            </div>
            {isAdmin && <TeamAdminBar key={`${data.team.id}:${data.team.name}`} team={data.team} />}

            <TeamReadSection range={range} teamId={data.team.id} />
            <TeamLedger data={data} />
            <AgainstOrg data={data} now={now} prior={prior} />
            <WeeklySection data={data} now={now} prior={prior} />
            <RosterSection
              data={data}
              windowDays={list?.inferenceWindowDays ?? null}
              prior={prior}
              admin={isAdmin ? { otherTeams } : null}
            />
            <ReviewsAcross data={data} now={now} />
            <InferredFrom data={data} />
          </>
        )}
      </div>
    </div>
  );
}

function TeamLedger({ data }: { data: TeamDetailResponse }) {
  const c = data.team.current;
  const p = data.team.previous;
  const paceNow = paceIndex(c, data.norm.org.current);
  const paceBefore = paceIndex(p, data.norm.org.previous);
  return (
    <Ledger
      items={[
        {
          label: "Score",
          current: c.score,
          prior: p.score,
          note: `median person; org ${fmtNum(data.norm.org.current.score)}`,
          ...pointChange(c.score, p.score),
        },
        {
          label: "Pace vs. org",
          current: paceNow,
          prior: paceBefore,
          format: (v) => `${fmtNum(v)}%`,
          note: "merged per person; org = 100%",
          ...pointChange(paceNow, paceBefore),
        },
        {
          label: "PRs merged",
          current: c.prsMerged,
          prior: p.prsMerged,
          note: `${fmtNum(c.prsOpened)} opened`,
          ...changeText(c.prsMerged, p.prsMerged),
        },
        {
          label: "People",
          current: c.headcount,
          prior: p.headcount,
          note: `${fmtNum(c.activeCount)} with work`,
          ...changeText(c.headcount, p.headcount),
        },
        {
          label: "Open to merge",
          current: c.medianHoursToMerge,
          prior: p.medianHoursToMerge,
          format: fmtHours,
          note: "median",
          ...changeText(c.medianHoursToMerge, p.medianHoursToMerge, { minBaseForPct: 0 }),
        },
        {
          label: "Wait for review",
          current: c.medianHoursToFirstReview,
          prior: p.medianHoursToFirstReview,
          format: fmtHours,
          note: "median, first review",
          ...changeText(c.medianHoursToFirstReview, p.medianHoursToFirstReview, { minBaseForPct: 0 }),
        },
        {
          label: "Tickets done",
          current: c.ticketsDone,
          prior: p.ticketsDone,
          note: `${fmtNum(c.pointsDone)} pts, done only`,
          ...changeText(c.ticketsDone, p.ticketsDone),
        },
        {
          label: "Bug share",
          current: c.bugShare,
          prior: p.bugShare,
          format: fmtPct,
          note: "of merged PRs",
          ...pointChange(c.bugShare, p.bugShare),
        },
      ]}
    />
  );
}

function AgainstOrg({ data, now, prior }: { data: TeamDetailResponse; now: string; prior: string }) {
  const { team, orgMedian } = data;
  const c = team.current;
  const p = team.previous;
  return (
    <>
      <div className={ed.sect}>I. Against the org</div>
      <h2 className={ed.h2}>{team.name} against the median team</h2>
      <p className={ed.prose}>
        Rates, so the comparison holds whatever the team's size. The ledger above has the totals.
      </p>
      <Figure
        title={`${team.name} per person and review speed, ${now}`}
        sub={`Filled dot is ${team.name}; hollow dots are the org median and ${team.name} in ${prior}.`}
        method="Per person: merged PRs over people with work in the window. Times: team medians over merged PRs; first review by someone other than the author, bots excluded. Org median is across teams."
        table={{
          columns: ["Measure", team.name, "Org median", prior],
          rows: [
            ["Merged PRs per person", fmtNum(c.prsMergedPerPerson), fmtNum(orgMedian.prsMergedPerPerson), fmtNum(p.prsMergedPerPerson)],
            ["Open to merge", fmtHours(c.medianHoursToMerge), fmtHours(orgMedian.medianHoursToMerge), fmtHours(p.medianHoursToMerge)],
            ["Wait for review", fmtHours(c.medianHoursToFirstReview), fmtHours(orgMedian.medianHoursToFirstReview), fmtHours(p.medianHoursToFirstReview)],
            ["Reviewed before merge", fmtPct(c.reviewedBeforeMergeShare), fmtPct(orgMedian.reviewedBeforeMergeShare), fmtPct(p.reviewedBeforeMergeShare)],
          ],
        }}
      >
        <ReferenceDots
          ariaLabel={`${team.name} against the org median`}
          subjectLabel={team.name}
          rows={[
            {
              label: "Merged PRs per person",
              value: c.prsMergedPerPerson,
              format: (v) => fmtNum(v),
              references: [
                { label: "Org median", value: orgMedian.prsMergedPerPerson },
                { label: prior, value: p.prsMergedPerPerson },
              ],
            },
            {
              label: "Open to merge",
              value: c.medianHoursToMerge,
              format: fmtHours,
              lowerIsBetter: true,
              references: [
                { label: "Org median", value: orgMedian.medianHoursToMerge },
                { label: prior, value: p.medianHoursToMerge },
              ],
            },
            {
              label: "Wait for review",
              value: c.medianHoursToFirstReview,
              format: fmtHours,
              lowerIsBetter: true,
              references: [
                { label: "Org median", value: orgMedian.medianHoursToFirstReview },
                { label: prior, value: p.medianHoursToFirstReview },
              ],
            },
          ]}
        />
      </Figure>
    </>
  );
}

function WeeklySection({ data, now, prior }: { data: TeamDetailResponse; now: string; prior: string }) {
  const weeks = weeklySeries(data.weekly);
  if (weeks.length === 0) return null;
  const cur = weeks.filter((w) => !w.previous);
  const peak = [...cur].sort((a, b) => b.prsMerged - a.prsMerged)[0];
  return (
    <>
      <div className={ed.sect}>II. Week by week</div>
      <h2 className={ed.h2}>
        {peak && peak.prsMerged > 0
          ? `The busiest week of ${now} started ${fmtDay(peak.weekStart)}, with ${plural(peak.prsMerged, "merge")}`
          : `No merges yet in ${now}`}
      </h2>
      <p className={ed.prose}>
        Every week of both windows, so a slow start or a strong finish shows against
        {prior}.
      </p>
      <Figure
        title={`Merged PRs per week, ${prior} and ${now}`}
        sub={`${capitalize(now)} in black, ${prior} in gray.`}
        method="PRs by team members merged in each week, by merge date. Weeks start Monday; a part week at either end holds fewer days."
        table={{
          columns: ["Week of", "Window", "Merged"],
          rows: weeks.map((w) => [fmtDay(w.weekStart), w.previous ? prior : now, w.prsMerged]),
        }}
      >
        <Columns
          ariaLabel="Merged PRs per week"
          unit="merged"
          valueLabels
          points={weeks.map((w) => ({
            key: w.weekStart,
            value: w.prsMerged,
            label: fmtDay(w.weekStart),
            title: `Week of ${fmtDay(w.weekStart)}${w.previous ? `, ${prior}` : ""}`,
            muted: w.previous,
          }))}
        />
      </Figure>
    </>
  );
}

function RosterSection({
  data,
  windowDays,
  prior,
  admin,
}: {
  data: TeamDetailResponse;
  /** Days of work membership is inferred over; null until the list loads. */
  windowDays: number | null;
  prior: string;
  /** Admin controls, or null for everyone else. */
  admin: { otherTeams: Array<{ id: string; name: string }> } | null;
}) {
  const pin = usePinMember();
  const [searchParams] = useSearchParams();
  const suffix = searchParams.toString() ? `?${searchParams.toString()}` : "";
  const members = rosterOrder(data.members);
  const counts = rosterCounts(data.members);
  return (
    <>
      <div className={ed.sect}>III. The roster</div>
      <h2 className={ed.h2}>
        {plural(members.length, "person", "people")} on {data.team.name}
      </h2>
      <p className={ed.prose}>
        Leads first, then by name. Struck figures are from {prior}.
        {counts.shared > 0 && (
          <>
            {" "}
            <span className="num">{fmtNum(counts.shared)}</span> split their work with another team;
            they count here, on their primary team, and Also on lists the rest.
          </>
        )}
        {counts.pinned > 0 && (
          <>
            {" "}
            <span className="num">{fmtNum(counts.pinned)}</span> pinned here by hand, so the next
            inference run leaves them in place.
          </>
        )}
      </p>
      {pin.error && (
        <Banner tone="caution" onDismiss={() => pin.reset()}>
          Couldn't change the roster:{" "}
          {pin.error instanceof Error ? pin.error.message : String(pin.error)}
        </Banner>
      )}
      <div className={tbl.scroll}>
        <table className={tbl.table}>
          <thead>
            <tr>
              <th>Person</th>
              <th data-num title="Share of this person's work that landed in this team's JIRA project and repos">
                Share
              </th>
              <th data-num>Merged</th>
              <th data-num>Reviewed</th>
              <th data-num>Tickets</th>
              <th data-num>Commits</th>
              <th>Also on</th>
              {admin && <th>Admin</th>}
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.developerId}>
                <td style={{ whiteSpace: "nowrap" }}>
                  <Link to={`/developers/${m.developerId}${suffix}`}>{m.displayName}</Link>
                  {m.isLead && (
                    <>
                      {" "}
                      <span className={tbl.label}>Lead</span>
                    </>
                  )}
                  {m.source === "pinned" && (
                    <span className={tbl.flag} data-tone="quiet" title="Pinned by an admin">
                      Pinned
                    </span>
                  )}
                  {m.shared && (
                    <span className={tbl.flag} data-tone="quiet" title="No single team holds most of their work">
                      Shared
                    </span>
                  )}
                </td>
                <td data-num>{shareText(m.share, m.source)}</td>
                <td data-num>
                  <Was prior={fmtNum(m.previous.prsMerged)} current={fmtNum(m.current.prsMerged)} />
                </td>
                <td data-num>
                  <Was prior={fmtNum(m.previous.reviewsGiven)} current={fmtNum(m.current.reviewsGiven)} />
                </td>
                <td data-num>
                  <Was prior={fmtNum(m.previous.ticketsDone)} current={fmtNum(m.current.ticketsDone)} />
                </td>
                <td data-num>
                  <Was prior={fmtNum(m.previous.commits)} current={fmtNum(m.current.commits)} />
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {m.alsoOn.length === 0 ? (
                    <span className={ed.muted}>–</span>
                  ) : (
                    m.alsoOn.map((o, i) => (
                      <span key={o.teamId}>
                        {i > 0 ? ", " : ""}
                        <Link to={`/teams/${o.teamId}${suffix}`}>{o.name}</Link>{" "}
                        <span className={tbl.aside}>{fmtPct(o.share * 100)}</span>
                      </span>
                    ))
                  )}
                </td>
                {admin && (
                  <td>
                    <MemberActions
                      teamId={data.team.id}
                      member={m}
                      otherTeams={admin.otherTeams}
                      pin={pin}
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className={ed.method}>
        Share is the part of each person's{" "}
        {windowDays ? `last ${fmtNum(windowDays)} days of work` : "recent work"} that landed in this
        team's JIRA project and repos; “via reviews” marks someone placed by who they review with. Tickets count done
        issues only, so members add up to the team total. People are listed, not ranked; see
        Developers for individual comparisons.
        {admin && (
          <>
            {" "}
            Move pins someone to another team and drops their inferred place here; a pin set by
            hand on another team stays.
          </>
        )}
      </p>
    </>
  );
}

function ReviewsAcross({ data, now }: { data: TeamDetailResponse; now: string }) {
  const given = [...data.reviewsGivenTo].filter((r) => r.count > 0).sort((a, b) => b.count - a.count);
  const got = [...data.reviewsReceivedFrom].filter((r) => r.count > 0).sort((a, b) => b.count - a.count);
  const totalGiven = given.reduce((s, r) => s + r.count, 0);
  const totalGot = got.reduce((s, r) => s + r.count, 0);
  const name = data.team.name;
  return (
    <>
      <div className={ed.sect}>IV. Across teams</div>
      <h2 className={ed.h2}>
        {totalGiven === 0 && totalGot === 0
          ? `${name} kept its reviews in house in ${now}`
          : `${name} gave ${plural(totalGiven, "review")} to other teams and got ${fmtNum(totalGot)} back`}
      </h2>
      {(given.length > 0 || got.length > 0) && (
        <div className={ed.halves}>
          {given.length > 0 ? (
            <Figure
              title={`Reviews ${name} gave other teams`}
              method="Reviews by team members on PRs authored by another team's members, in the window. Everyone counts on their primary team."
              table={{ columns: ["Team", "Reviews"], rows: given.map((r) => [r.name, r.count]) }}
            >
              <RankedBars
                ariaLabel={`Reviews ${name} gave other teams`}
                width={420}
                rows={given.map((r) => ({ id: r.teamId, label: r.name, value: r.count }))}
              />
            </Figure>
          ) : (
            <p className={ed.prose}>{name} reviewed no other team's PRs.</p>
          )}
          {got.length > 0 ? (
            <Figure
              title={`Reviews ${name} got from other teams`}
              method="Reviews on PRs authored by team members, given by another team's members, in the window. Everyone counts on their primary team."
              table={{ columns: ["Team", "Reviews"], rows: got.map((r) => [r.name, r.count]) }}
            >
              <RankedBars
                ariaLabel={`Reviews ${name} got from other teams`}
                width={420}
                rows={got.map((r) => ({ id: r.teamId, label: r.name, value: r.count }))}
              />
            </Figure>
          ) : (
            <p className={ed.prose}>No other team reviewed {name}'s PRs.</p>
          )}
        </div>
      )}
    </>
  );
}

function InferredFrom({ data }: { data: TeamDetailResponse }) {
  const t = data.team;
  return (
    <>
      <div className={ed.sect}>Where this team comes from</div>
      <p className={ed.prose}>
        {sourceSentence({ signal: t.signal, jiraProjectKey: t.jiraProjectKey, repoCount: t.repos.length })}{" "}
        Pinned people stay on the team when it is worked out again.
      </p>
      <dl className={ed.defs}>
        <div>
          <dt>JIRA project</dt>
          <dd className={ed.num}>{t.jiraProjectKey ?? "–"}</dd>
        </div>
        <div>
          <dt>Repos</dt>
          <dd className={ed.num}>
            {t.repos.length === 0 ? "–" : t.repos.map((r) => <div key={r}>{r}</div>)}
          </dd>
        </div>
        <div>
          <dt>Leads</dt>
          <dd>{t.leads.length === 0 ? "None named" : t.leads.map((l) => l.displayName).join(", ")}</dd>
        </div>
      </dl>
    </>
  );
}
